import { Environment, EnvironmentService } from '@galaxy/core';
import { inject, Injectable } from '@angular/core';
import { IntegrationType } from '../model/connection-integration-detail';
import { LegacyIntegrationType } from '../model/social-connections.interface';

@Injectable({
  providedIn: 'root',
})
export class LegacyDomainService {
  private readonly environment = inject(EnvironmentService);

  // Below is legacy logic of building the google based product's auth endpoint
  //https://github.com/vendasta/galaxy/blob/2a06a62a0b79deab019117005f7c886b0e4b89ad/apps/business-center-client/src/app/social-connections/social-connections.service.ts#L391-L405
  buildGoogleAuthUrl(
    partnerId: string,
    accountGroupId: string,
    productType: string,
    nextUrl: URL,
    returnURL: string,
  ): string {
    // Determine if the first query parameter needs an '&' or a '?'
    const queryParamCharacter = nextUrl.search ? '&' : '?';

    // Replace AG-xxxx with the actual accountGroupId in the URL path
    const newUrl = nextUrl.href.replace(/\/AG-\w+/g, `/${accountGroupId}`);

    // Encode the return URL
    const returnUrl = encodeURIComponent(newUrl + queryParamCharacter + `type=${productType}&message=VBC`);

    // Construct the base URI for Google Search Console authentication
    let baseUri: string;
    if (productType === 'google-search-console') {
      baseUri = this.googleSearchConsoleHost() + '/oauth/v1/auth-user';
    } else if (productType === 'google-analyticsGA4') {
      baseUri = this.googleAnalyticsHost() + '/oauth/v1/auth-user';
    } else {
      throw new Error(`Unsupported productType: ${productType}`);
    }

    // Encode URI parameters
    const uriEncodedParams = `partner_id=${partnerId}&business_id=${accountGroupId}&return_url=${returnUrl}`;

    if (returnURL) {
      const encodedReturnUrl = encodeURIComponent(
        `${newUrl}${queryParamCharacter}type=${productType}&message=VBC&returnUrl=${returnURL}`,
      );
      return `${baseUri}?partner_id=${partnerId}&business_id=${accountGroupId}&return_url=${encodedReturnUrl}`;
    }

    // Construct and return the final URL
    return `${baseUri}?${uriEncodedParams}`;
  }

  private googleSearchConsoleHost(): string {
    switch (this.environment.getEnvironment()) {
      case Environment.LOCAL:
        return 'http://localhost:11001';
      case Environment.DEMO:
        return 'https://google-search-console-demo.apigateway.co';
      case Environment.PROD:
        return 'https://google-search-console-prod.apigateway.co';
    }
    return 'https://google-search-console-demo.apigateway.co';
  }

  private googleAnalyticsHost(): string {
    switch (this.environment.getEnvironment()) {
      case Environment.LOCAL:
        return 'http://localhost:11001';
      case Environment.DEMO:
        return 'https://google-analytics-api-demo.apigateway.co';
      case Environment.PROD:
        return 'https://google-analytics-api-prod.apigateway.co';
    }
    return 'https://google-analytics-api-demo.apigateway.co';
  }

  getLegacyIntegrationType(integrationType: IntegrationType): LegacyIntegrationType {
    switch (integrationType) {
      case IntegrationType.GOOGLEMYBUSINESS:
      case IntegrationType.GOOGLESEARCHCONSOLE:
      case IntegrationType.GOOGLEANALYTICS:
        return LegacyIntegrationType.GOOGLEPLUS;
      case IntegrationType.FACEBOOK:
        return LegacyIntegrationType.FACEBOOK;
      case IntegrationType.GOOGLEMEET:
        return LegacyIntegrationType.GOOGLEMEET;
      case IntegrationType.MICROSOFTTEAMS:
        return LegacyIntegrationType.MICROSOFTTEAMS;
      case IntegrationType.ZOOMMEET:
        return LegacyIntegrationType.ZOOMMEET;
      case IntegrationType.QUICKBOOKSONLINEPERSONAL:
        return LegacyIntegrationType.QUICKBOOKS;
      case IntegrationType.INSTAGRAM:
        return LegacyIntegrationType.INSTAGRAM;
      default:
        throw new Error('Unsupported integration type');
    }
  }

  getLegacyTypeAsDisplayName(integrationType: IntegrationType) {
    switch (integrationType) {
      case IntegrationType.GOOGLEMYBUSINESS:
        return 'Google My Business';
      case IntegrationType.FACEBOOK:
        return IntegrationType.FACEBOOK;
      default:
        throw new Error('Unsupported integration type');
    }
  }
}
